/**
 * Size Guide Modal Component
 * Handles the opening/closing of size guide popups with accessibility features
 */

class SizeGuideModal {
  constructor() {
    this.init();
  }

  init() {
    // Get DOM elements
    this.button = document.querySelector('[data-size-guide-trigger]');
    this.modal = document.querySelector('[data-size-guide-modal]');
    this.closeButton = document.querySelector('[data-size-guide-close]');
    this.backdrop = document.querySelector('[data-size-guide-backdrop]');
    
    // Early return if elements don't exist
    if (!this.button || !this.modal) return;
    
    // Bind methods
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    
    // Initialize event listeners
    this.attachEventListeners();
    
    // Handle image loading
    this.handleImageLoading();
  }

  attachEventListeners() {
    // Open modal
    this.button.addEventListener('click', this.openModal);
    
    // Close modal
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.closeModal);
    }
    
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.closeModal);
    }
    
    // Keyboard support
    document.addEventListener('keydown', this.handleKeydown);
  }

  openModal() {
    // Prevent body scroll
    this.preventBodyScroll();
    
    // Show modal
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('size-guide-modal-open');
    
    // Focus management
    if (this.closeButton) {
      this.closeButton.focus();
    }
    
    // Trap focus within modal
    this.trapFocus(this.modal);
    
    // Announce to screen readers
    this.announceModalOpen();
  }

  closeModal() {
    // Restore body scroll
    this.restoreBodyScroll();
    
    // Hide modal
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('size-guide-modal-open');
    
    // Return focus to button
    this.button.focus();
    
    // Remove focus trap
    this.removeFocusTrap();
  }

  handleKeydown(event) {
    // Close modal on Escape key
    if (event.key === 'Escape' && this.modal.classList.contains('is-open')) {
      this.closeModal();
    }
  }

  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Store the focus trap handler for later removal
    this.focusTrapHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    };

    element.addEventListener('keydown', this.focusTrapHandler);
  }

  removeFocusTrap() {
    if (this.focusTrapHandler && this.modal) {
      this.modal.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  preventBodyScroll() {
    // Store current scroll position
    this.scrollY = window.scrollY;
    
    // Apply styles to prevent scrolling
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.width = '100%';
  }

  restoreBodyScroll() {
    // Remove scroll prevention styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    
    // Restore scroll position
    if (this.scrollY !== undefined) {
      window.scrollTo(0, this.scrollY);
    }
  }

  handleImageLoading() {
    const images = this.modal.querySelectorAll('img[loading="lazy"]');
    
    images.forEach(img => {
      // Add loaded class when image loads
      img.addEventListener('load', () => {
        img.classList.add('loaded');
      });
      
      // Handle error case
      img.addEventListener('error', () => {
        img.classList.add('loaded'); // Still remove loading state
        console.warn('Size guide image failed to load:', img.src);
      });
      
      // If image is already loaded (cached)
      if (img.complete) {
        img.classList.add('loaded');
      }
    });
  }

  announceModalOpen() {
    // Create or update announcement for screen readers
    let announcement = document.getElementById('size-guide-announcement');
    
    if (!announcement) {
      announcement = document.createElement('div');
      announcement.id = 'size-guide-announcement';
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'visually-hidden';
      document.body.appendChild(announcement);
    }
    
    announcement.textContent = 'Size guide opened. Press Escape to close.';
  }

  // Cleanup method
  destroy() {
    // Remove event listeners
    if (this.button) {
      this.button.removeEventListener('click', this.openModal);
    }
    
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.closeModal);
    }
    
    if (this.backdrop) {
      this.backdrop.removeEventListener('click', this.closeModal);
    }
    
    document.removeEventListener('keydown', this.handleKeydown);
    
    // Remove focus trap
    this.removeFocusTrap();
    
    // Restore body scroll if modal was open
    if (this.modal && this.modal.classList.contains('is-open')) {
      this.restoreBodyScroll();
      document.body.classList.remove('size-guide-modal-open');
    }
  }
}

// Utility function to add visually-hidden class if it doesn't exist
function addVisuallyHiddenStyles() {
  if (!document.querySelector('style[data-size-guide-a11y]')) {
    const style = document.createElement('style');
    style.setAttribute('data-size-guide-a11y', '');
    style.textContent = `
      .visually-hidden {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }
    `;
    document.head.appendChild(style);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Add accessibility styles
  addVisuallyHiddenStyles();
  
  // Initialize size guide modal
  window.sizeGuideModal = new SizeGuideModal();
});

// Handle page navigation (for SPA-like behavior)
document.addEventListener('shopify:section:load', function() {
  if (window.sizeGuideModal) {
    window.sizeGuideModal.destroy();
  }
  window.sizeGuideModal = new SizeGuideModal();
});

// Cleanup on page unload
window.addEventListener('beforeunload', function() {
  if (window.sizeGuideModal) {
    window.sizeGuideModal.destroy();
  }
}); 